'use client';

import {
  CameraIcon,
  Cross2Icon,
  FileIcon,
  FileTextIcon,
  PlayIcon,
  UploadIcon,
} from '@radix-ui/react-icons';
import type { Dispatch, HTMLAttributes, ReactNode, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Dropzone, { type DropzoneProps, type FileRejection } from 'react-dropzone';

import { Button } from '../../components/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/popover';
import { toast } from '../../components/useToast';
import { cn, formatBytes, isFileWithPreview } from '../../utils';
import { appendHeicPreview } from '../../utils/heicConverter';
import { useControllableState } from '../../utils/hooks';

// File type category detection
type FileCategory = 'image' | 'video' | 'audio' | 'pdf' | 'unknown';

const getFileCategory = (mimeType: string): FileCategory => {
  const type = mimeType.toLowerCase();
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type === 'application/pdf') return 'pdf';
  return 'unknown';
};

// Icon configuration by file category
const FILE_ICON_CONFIG: Record<FileCategory, { icon: ReactNode; containerClass: string }> = {
  image: {
    icon: <FileIcon className="size-6 text-gray-500" />,
    containerClass: 'bg-gray-100',
  },
  video: {
    icon: <PlayIcon className="size-6 text-gray-600" />,
    containerClass: 'bg-gray-100',
  },
  audio: {
    icon: <PlayIcon className="size-6 text-blue-500" />,
    containerClass: 'bg-blue-50',
  },
  pdf: {
    icon: <FileTextIcon className="size-6 text-red-500" />,
    containerClass: 'bg-red-50',
  },
  unknown: {
    icon: <FileIcon className="size-6 text-gray-500" />,
    containerClass: 'bg-gray-100',
  },
};

const getFileIconConfig = (file: File) => {
  const category = getFileCategory(file.type);
  return FILE_ICON_CONFIG[category];
};

interface FileUploaderProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Value of the uploader.
   * @type File[]
   * @default undefined
   * @example value={files}
   */
  value?: File[];

  /**
   * Function to be called when the value changes.
   * @type Dispatch<SetStateAction<File[]>>
   * @default undefined
   * @example onValueChange={(files) => setFiles(files)}
   */
  onValueChange?: Dispatch<SetStateAction<File[]>>;

  /**
   * Function to be called when files are uploaded.
   * @type (files: File[]) => Promise<void>
   * @default undefined
   * @example onUpload={(files) => uploadFiles(files)}
   */
  onUpload?: (files: File[]) => void;

  /**
   * Accepted file types for the uploader.
   * @type { [key: string]: string[]}
   * @default
   * ```ts
   * { "image/*": [] }
   * ```
   * @example accept={["image/png", "image/jpeg"]}
   */
  accept?: DropzoneProps['accept'];

  /**
   * Maximum file size for the uploader.
   * @type number | undefined
   * @default 1024 * 1024 * 2 // 2MB
   * @example maxSize={1024 * 1024 * 2} // 2MB
   */
  maxSize?: DropzoneProps['maxSize'];

  /**
   * Maximum number of files for the uploader.
   * @type number | undefined
   * @default 1
   * @example maxFiles={5}
   */
  maxFiles?: DropzoneProps['maxFiles'];

  /**
   * Whether the uploader should accept multiple files.
   * @type boolean
   * @default false
   * @example multiple
   */
  multiple?: boolean;

  /**
   * Whether the uploader is disabled.
   * @type boolean
   * @default false
   * @example disabled
   */
  disabled?: boolean;

  /**
   * If true, displays as a button instead of a drag and drop area
   * @type boolean
   * @default false
   * @example noDrag
   */
  noDrag?: boolean;

  /**
   * If true, displays camera and browse buttons instead of dropzone
   * @type boolean
   * @default false
   * @example useButtons
   */
  useButtons?: boolean;

  /**
   * Custom button labels
   * @type { camera?: string; browse?: string }
   * @default { camera: "Take Photo", browse: "Browse Files" }
   */
  buttonLabels?: {
    camera?: string;
    browse?: string;
  };

  /**
   * If true, hide uploaded files
   * @type boolean
   * @default false
   * @example hideUploadedFiles
   */
  hideUploadedFiles?: boolean;
}

export function FileUploader(props: FileUploaderProps) {
  const {
    value: valueProp,
    onValueChange,
    onUpload,
    accept = { 'image/*': [] },
    maxSize = 1024 * 1024 * 20,
    maxFiles = 1,
    multiple = false,
    disabled = false,
    noDrag = false,
    useButtons = false,
    buttonLabels = { camera: 'Take Photo', browse: 'Browse Files' },
    className,
    hideUploadedFiles = false,
    ...dropzoneProps
  } = props;

  const [files, setFiles] = useControllableState<File[]>({
    prop: undefined,
    onChange: onValueChange,
    defaultProp: [],
  });

  const [isProcessing, setIsProcessing] = useState(false);

  // Process initial files if provided
  useEffect(() => {
    const processInitialFiles = async () => {
      if (valueProp && valueProp.length > 0) {
        setIsProcessing(true);
        try {
          const processedFiles = await Promise.all(
            valueProp.map((file: File) => appendHeicPreview(file)),
          );
          setFiles(processedFiles);
        } catch (error) {
          console.error('Error processing initial files:', error);
        } finally {
          setIsProcessing(false);
        }
      }
    };

    // Only process initial files if there are no files yet
    // This prevents an infinite update loop
    if ((!files || files.length === 0) && valueProp && valueProp.length > 0) {
      processInitialFiles();
    }
  }, [valueProp, files, setFiles]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (!multiple && maxFiles === 1 && acceptedFiles.length > 1) {
        toast({ variant: 'destructive', title: 'Cannot upload more than 1 file at a time' });
        return;
      }

      if ((files?.length ?? 0) + acceptedFiles.length > maxFiles) {
        toast({ title: `Cannot upload more than ${maxFiles} files`, variant: 'destructive' });
        return;
      }

      setIsProcessing(true);

      try {
        // Process all files for HEIC conversion if needed
        const processedFiles = await Promise.all(
          acceptedFiles.map((file) => appendHeicPreview(file)),
        );

        const updatedFiles = files ? [...files, ...processedFiles] : processedFiles;

        setFiles(updatedFiles);

        if (rejectedFiles.length > 0) {
          rejectedFiles.forEach(({ file }) => {
            toast({ title: `File ${file.name} was rejected`, variant: 'destructive' });
          });
        }

        if (onUpload && updatedFiles.length > 0 && updatedFiles.length <= maxFiles) {
          onUpload(updatedFiles);
        }
      } catch (error) {
        console.error('Error processing files:', error);
        toast({ variant: 'destructive', title: 'Error processing files' });
      } finally {
        setIsProcessing(false);
      }
    },
    [files, maxFiles, multiple, onUpload, setFiles],
  );

  function onRemove(index: number) {
    if (!files) return;
    const newFiles = files.filter((_: File, i: number) => i !== index);
    setFiles(newFiles);
    // Only call onValueChange if it exists
    if (onValueChange) {
      onValueChange(newFiles);
    }
  }

  // Revoke preview url when component unmounts
  useEffect(() => {
    return () => {
      // Only try to clean up if files exist
      if (files && files.length > 0) {
        // Create a local copy of the files array to avoid
        // React state updates during cleanup
        const filesToCleanup = [...files];
        filesToCleanup.forEach((file: File) => {
          if (isFileWithPreview(file)) {
            URL.revokeObjectURL(file.preview);
          }
        });
      }
    };
    // eslint-disable-next-line react-compiler/react-compiler
    // Empty dependency array means this only runs on mount/unmount
  }, [files]);

  const isDisabled = disabled || (files?.length ?? 0) >= maxFiles || isProcessing;

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const browseInputRef = useRef<HTMLInputElement>(null);
  const dropzoneInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="relative flex flex-col gap-6 overflow-hidden">
      {useButtons ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="relative">
            <input
              ref={cameraInputRef}
              type="file"
              accept={Object.entries(accept || {})
                .map(([mimeType, extensions]) =>
                  extensions.length > 0 ? `${mimeType},${extensions.join(',')}` : mimeType,
                )
                .join(',')}
              capture="environment"
              multiple={maxFiles > 1 || multiple}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                onDrop(files, []);
              }}
              disabled={isDisabled}
              className="sr-only"
              id="camera-input"
            />
            <Button
              type="button"
              variant="outline"
              disabled={isDisabled}
              className="flex h-12 w-full items-center justify-center gap-2 border-2 border-dashed border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => cameraInputRef.current?.click()}
              asChild
            >
              <label>
                <CameraIcon className="h-5 w-5" />
                {buttonLabels.camera}
              </label>
            </Button>
          </div>

          <div className="relative">
            <input
              ref={browseInputRef}
              type="file"
              accept={Object.entries(accept || {})
                .map(([mimeType, extensions]) =>
                  extensions.length > 0 ? `${mimeType},${extensions.join(',')}` : mimeType,
                )
                .join(',')}
              multiple={maxFiles > 1 || multiple}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                onDrop(files, []);
              }}
              disabled={isDisabled}
              className="sr-only"
              id="browse-input"
            />
            <Button
              type="button"
              variant="outline"
              disabled={isDisabled}
              className="flex h-12 w-full items-center justify-center gap-2 border-2 border-dashed border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => browseInputRef.current?.click()}
              asChild
            >
              <label>
                <UploadIcon className="h-5 w-5" />
                {buttonLabels.browse}
              </label>
            </Button>
          </div>
        </div>
      ) : (
        <Dropzone
          onDrop={onDrop}
          accept={accept}
          maxSize={maxSize}
          maxFiles={maxFiles}
          multiple={maxFiles > 1 || multiple}
          disabled={isDisabled}
        >
          {({ getRootProps, getInputProps, isDragActive }) =>
            noDrag ? (
              <div className="flex w-full">
                <div {...getRootProps()} className="w-full" tabIndex={-1}>
                  <input {...getInputProps()} tabIndex={-1} />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isDisabled}
                    className={cn('flex w-full items-center justify-center gap-2 py-2', className)}
                  >
                    <UploadIcon className="size-4" />
                    Upload Media
                  </Button>
                </div>
              </div>
            ) : (
              <div
                {...getRootProps()}
                className={cn(
                  'border-muted-foreground/25 hover:bg-muted/25 focus:border-primary group relative grid h-52 w-full cursor-pointer place-items-center rounded-lg border-2 border-dashed px-5 py-2.5 text-center transition',
                  'ring-offset-background focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                  isDragActive && 'border-muted-foreground/50',
                  isDisabled && 'pointer-events-none opacity-60',
                  className,
                )}
                {...dropzoneProps}
                tabIndex={0}
                role="button"
                aria-label="Upload files"
                onClick={() => {
                  dropzoneInputRef.current?.click();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    dropzoneInputRef.current?.click();
                  }
                }}
              >
                <input {...getInputProps()} ref={dropzoneInputRef} tabIndex={-1} />
                {isDragActive ? (
                  <div className="flex flex-col items-center justify-center gap-4 sm:px-5">
                    <div className="rounded-full border border-dashed p-3">
                      <UploadIcon className="text-muted-foreground size-7" aria-hidden="true" />
                    </div>
                    <p className="text-muted-foreground font-medium">Drop the files here</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-4 sm:px-5">
                    <div className="rounded-full border border-dashed p-3">
                      <UploadIcon className="text-muted-foreground size-7" aria-hidden="true" />
                    </div>
                    <div className="space-y-px">
                      <p className="text-muted-foreground font-medium">
                        Drag {`'n'`} drop files here, or click to select files
                      </p>
                      <p className="text-muted-foreground/70 text-sm">
                        You can upload
                        {maxFiles > 1
                          ? ` ${maxFiles === Infinity ? 'multiple' : maxFiles}
                          files (up to ${formatBytes(maxSize)} each)`
                          : ` a file with ${formatBytes(maxSize)}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )
          }
        </Dropzone>
      )}
      {isProcessing && (
        <div className="text-muted-foreground text-center text-sm">Processing files...</div>
      )}
      {files?.length && !hideUploadedFiles ? (
        <div className="h-fit w-full overflow-y-auto px-3">
          <div className="max-h-48 space-y-4">
            {files.map((file: File, index: number) => (
              <FileCard
                disabled={disabled || isProcessing}
                key={index}
                file={file}
                onRemove={() => onRemove(index)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface FileCardProps {
  file: File;
  onRemove: () => void;
  disabled?: boolean;
}

function FileCard({ file, onRemove, disabled }: FileCardProps) {
  const fileType = file.type.toLowerCase();
  const isImage = fileType.startsWith('image/');

  const renderFilePreview = () => {
    // Images with preview - show image thumbnail
    if (isImage && isFileWithPreview(file)) {
      return (
        <Popover>
          <PopoverTrigger asChild>
            <img
              src={file.preview}
              alt={file.name}
              width={48}
              height={48}
              loading="lazy"
              className="aspect-square shrink-0 cursor-pointer rounded-md object-cover"
            />
          </PopoverTrigger>
          <PopoverContent className="w-max">
            <img
              src={file.preview}
              alt={file.name}
              className="max-h-80 max-w-[80vw] rounded-md object-cover"
            />
          </PopoverContent>
        </Popover>
      );
    }

    // Non-image files or images without preview - show appropriate icon
    const { icon, containerClass } = getFileIconConfig(file);
    return (
      <div
        className={cn(
          'flex size-12 shrink-0 items-center justify-center rounded-md',
          containerClass,
        )}
      >
        {icon}
      </div>
    );
  };

  return (
    <div className="relative flex items-center space-x-4">
      <div className="flex flex-1 space-x-4">
        {renderFilePreview()}
        <div className="flex w-full flex-col gap-2">
          <div className="space-y-px">
            <p className="text-foreground/80 line-clamp-1 break-all text-sm font-medium">
              {file.name}
            </p>
            <p className="text-muted-foreground text-xs">{formatBytes(file.size)}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-7"
          disabled={disabled}
          onClick={onRemove}
        >
          <Cross2Icon className="size-4 " aria-hidden="true" />
          <span className="sr-only">Remove file</span>
        </Button>
      </div>
    </div>
  );
}
